/**
 * Visual design tokens for the "Verified by Dealstack" stamp. Single source of truth
 * shared by the backend renderer and front-end preview so they can't drift. Numeric
 * values are in PDF points (each renderer scales at use site); colours are hex.
 */

export const PALETTE = {
	// Brand accent (border, header text + shield).
	brand: '#0d5ef2',
	lightGrey: '#cfcfcf',
	textDark: '#212121',
	textMuted: '#999999',
	white: '#ffffff',
} as const;

export const STAMP_DESIGN = {
	border: { width: 1.5, radius: 8, color: PALETTE.brand },
	header: {
		fontSize: 11,
		leftOffset: 24,
		paddingX: 8,
		iconSize: 12,
		textColor: PALETTE.brand,
	},
	signature: {
		insetX: 18,
		// Scribble-band height; kept clear of the divider even when a detail line
		// lifts it a row. Auto-scales to fit and stays the dominant element.
		maxHeight: 28,
		// Top clearance guard: signature is bottom-anchored to the divider, this
		// only kicks in so a tall drawing can't climb into the header.
		topPadding: 10,
		// Gap between signature bottom and divider so the scribble rests on the line.
		bottomGap: 3,
	},
	// `topOffset` = distance from the BOTTOM inner edge to the divider line.
	divider: { topOffset: 42, insetX: 18, color: PALETTE.lightGrey },
	meta: {
		// Category column ("Name", "Address", …) reads smaller and muted; its value
		// reads larger, bold, and dark — a two-column table rather than inline "Label: value" text.
		labelFontSize: 7,
		valueFontSize: 8.5,
		// Distance from the BOTTOM inner edge to the Signed row's baseline.
		topOffset: 14,
		// Per-line gap inside the metadata stack — generous, so rows read as a table.
		lineGap: 6,
		// Gap after a label's trailing colon — half a space (a space is 1.97pt here), so the
		// colon does the dividing and everything else goes to the value. At 0 the value's
		// bold glyphs collide with the colon.
		labelGap: 1,
		labelColor: PALETTE.textMuted,
		valueColor: PALETTE.textDark,
	},
	id: {
		fontSize: 7,
		leftOffset: 48,
		labelPaddingX: 6,
		labelColor: PALETTE.textDark,
	},
} as const;

/** Witness address text word-wraps to at most this many lines before the overflow is
 *  ellipsized onto the last line (see stampRenderer.wrapTextToLines). Shared because it
 *  also sizes the witness box itself — see STAMP_HEIGHT_WITNESS_PT in stampGeometry, the
 *  height the reflow renderer reserves before any stamp exists. */
export const MAX_ADDRESS_LINES = 4;
